import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { usePreferences } from "./usePreferences.js";

export const GRID_SIZE_MIN = 12;
export const GRID_SIZE_MAX = 48;
export const GRID_SIZE_STEP = 6;
export const DEFAULT_GRID_SIZE = 24;

interface GridSettingsContextValue {
  gridSize: number;
  setGridSize: (gridSize: number) => void;
}

const GridSettingsContext = createContext<GridSettingsContextValue | null>(null);

/** Clamps and snaps a stored value, so an out-of-range row can't break the canvas. */
function normalise(stored: number | null): number {
  if (stored === null || !Number.isFinite(stored) || stored < GRID_SIZE_MIN || stored > GRID_SIZE_MAX) {
    return DEFAULT_GRID_SIZE;
  }
  return Math.round(stored / GRID_SIZE_STEP) * GRID_SIZE_STEP;
}

export function GridSettingsProvider({ children }: { children: ReactNode }) {
  const { preferences, loaded, save } = usePreferences();
  // Local mirror so dragging the slider is smooth; the stored value takes over
  // once the session answers (see usePreferences for why nothing is cached).
  const [localSize, setLocalSize] = useState<number | null>(null);
  const [lastStored, setLastStored] = useState(preferences.gridSize);

  // See ThemeProvider: dropped during render, not in an effect, so a newly
  // arrived server value always wins over the local mirror.
  if (loaded && preferences.gridSize !== lastStored) {
    setLastStored(preferences.gridSize);
    setLocalSize(null);
  }

  const gridSize = localSize ?? normalise(preferences.gridSize);

  const setGridSize = useCallback(
    (next: number) => {
      setLocalSize(next);
      void save({ gridSize: next });
    },
    [save]
  );

  return (
    <GridSettingsContext.Provider value={{ gridSize, setGridSize }}>
      {children}
    </GridSettingsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGridSettings(): GridSettingsContextValue {
  const context = useContext(GridSettingsContext);
  if (!context) throw new Error("useGridSettings must be used inside GridSettingsProvider");
  return context;
}
